import React from 'react';
import { useSelector } from 'react-redux';

import { useFilters } from './hooks/useFilters';
import { useTeamFilters } from './hooks/useTeamFilters';
import { useScrollRestore } from './hooks/useScrollRestore';
import { usePractitionerActions } from './hooks/usePractitionerActions';
import { mapGhlMemberToPerson } from './helpers';
import {
  SectionContainer,
  SectionTitle,
  TeamGrid,
  TeamMemberGrid,
  LoadMoreContainer,
  LoadMoreButton,
} from './styles';
import { FilterSearchContext } from './components/FilterDropdown';
import FiltersSection from './components/FiltersSection';
import TeamMemberCard from './components/TeamMemberCard';
import TeamSectionSkeleton from './components/TeamSectionSkeleton';
import {
  TeamSectionError,
  TeamSectionEmpty,
  TeamSectionNoResults,
} from './components/TeamSectionStates';
import FreeConsultationDialog from '../../../../../components/FreeConsultationDialog';

const INITIAL_LIMIT = 6;
const LIMIT_INCREMENT = 6;

const TeamSection = () => {
  const { filters: filtersConfig } = useFilters();
  const { handlePractitionerClick, handleBookConsultation, freeConsultationDialog } =
    usePractitionerActions();
  const {
    data: ghlData,
    loading,
    error: reduxError,
  } = useSelector((state) => state.practitioners);

  const people = React.useMemo(() => {
    if (!ghlData || ghlData.length === 0) return [];
    // Map then sort alphabetically by last name so the team page has a stable,
    // fair order regardless of whether other views shuffle the same underlying
    // data for exposure fairness. Last name is derived from the mapped
    // `name` field (mapGhlMemberToPerson already joins first+last or uses
    // the GHL User's full name).
    const lastNameKey = (person) => {
      const name = (person.name || '').trim();
      if (!name) return '';
      const parts = name.split(/\s+/);
      return (parts[parts.length - 1] || '').toLowerCase();
    };
    const mapped = ghlData.map(mapGhlMemberToPerson);
    return mapped.slice().sort((a, b) => {
      const aKey = lastNameKey(a);
      const bKey = lastNameKey(b);
      return aKey.localeCompare(bKey);
    });
  }, [ghlData]);

  const {
    newFilters,
    filterSearchTerms,
    setFilterSearchTerms,
    openFilterKey,
    setOpenFilterKey,
    handleNewFilter,
    filtered,
    scrollPositionToRestore,
  } = useTeamFilters(people, filtersConfig);

  const [limit, setLimit] = React.useState(INITIAL_LIMIT);

  useScrollRestore(scrollPositionToRestore);

  const setLimitHandler = () => setLimit((prev) => prev + LIMIT_INCREMENT);

  const shown = filtered.slice(0, limit);
  const hasMore = limit < filtered.length;

  if (loading) {
    return <TeamSectionSkeleton filtersConfig={filtersConfig} />;
  }

  if (reduxError) {
    return <TeamSectionError />;
  }

  if (!people || people.length === 0) {
    return <TeamSectionEmpty />;
  }

  return (
    <FilterSearchContext.Provider
      value={{
        searchTerms: filterSearchTerms,
        setSearchTerms: setFilterSearchTerms,
        closeMenu: () => setOpenFilterKey(null),
      }}
    >
      <SectionContainer>
        <SectionTitle>Meet Our Practitioners</SectionTitle>

        {filtersConfig.length > 0 && (
        <FiltersSection
          filtersConfig={filtersConfig}
          newFilters={newFilters}
          filterSearchTerms={filterSearchTerms}
          setFilterSearchTerms={setFilterSearchTerms}
          openFilterKey={openFilterKey}
          setOpenFilterKey={setOpenFilterKey}
          handleNewFilter={handleNewFilter}
        />
        )}
        {filtered.length === 0 ? (
          <TeamSectionNoResults />
        ) : (
          <TeamGrid container>
            {shown.map((p) => (
              <TeamMemberGrid item xs="auto" key={p.id}>
                <TeamMemberCard
                  practitioner={p}
                  onMeetClick={handlePractitionerClick(p)}
                  onBookConsultation={handleBookConsultation(p)}
                />
              </TeamMemberGrid>
            ))}
          </TeamGrid>
        )}

        {hasMore && (
          <LoadMoreContainer>
            <LoadMoreButton variant="outlined" text="Load More" onClick={setLimitHandler} />
          </LoadMoreContainer>
        )}
        <FreeConsultationDialog
          open={freeConsultationDialog.showDialog}
          cancelling={freeConsultationDialog.cancelling}
          onDismiss={freeConsultationDialog.dismiss}
          onCancelAndProceed={freeConsultationDialog.cancelAndProceed}
        />
      </SectionContainer>
    </FilterSearchContext.Provider>
  );
};

export default TeamSection;
